/**
 * Phase 8 — 📹 Video Live Beams
 * Phase 21.1f — Live Media Recovery (Feb 2026)
 * --------------------------------
 * useLiveKitRoom: connects to a LiveKit Cloud room as the role we get
 * from /live/rooms/{roomId}/livekit-token. Listeners subscribe-only;
 * host/guests can toggle camera + microphone.
 *
 * Designed to coexist with our existing app WebSocket (chat / reactions
 * / gifts / quests) — LiveKit handles ONLY the media transport.
 *
 * 21.1f hardening:
 *   • Surface the precise connect error in `failedReason` so we can debug
 *     production issues (token / ICE / permissions / project).
 *   • `retry()` action so the UI can offer a one-tap reconnect when the
 *     auto-reconnect gives up.
 *   • Manual exponential-backoff retry (up to 4 attempts) for transient
 *     network blips before declaring `failed`.
 *   • Identity-collision-safe cleanup: cancel in-flight connect attempts
 *     and disconnect cleanly before remounting.
 *   • Optional `?lk=1` URL flag turns on verbose `console.log` tracing
 *     so production hosts can paste logs for diagnosis.
 *
 * Public API:
 *   const {
 *     state,          // 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'
 *     role, canPublish, livekitConfigured,
 *     localParticipant, remoteParticipants,
 *     toggleCamera, toggleMic, switchCamera,
 *     cameraEnabled, micEnabled, cameraDenied, micDenied,
 *     refreshToken,    // re-fetch token after a host promotion
 *     retry,           // 21.1f — one-tap reconnect
 *     failedReason,    // 21.1f — last connect error message (for UI/diagnostics)
 *     attempts,        // 21.1f — # of connect attempts so far
 *   } = useLiveKitRoom({ roomId, enabled });
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, Track, LocalVideoTrack, DisconnectReason } from "livekit-client";
import api from "@/lib/api";

const NOT_ALLOWED = ["NotAllowedError", "PermissionDeniedError"];
const MAX_AUTO_RETRIES = 4;

// iter_181 (P0) — Map LiveKit DisconnectReason enum values into the
// backend allow-list of client_disconnect_reasons. Any value not in the
// map falls back to "unknown" (which the backend also normalises).
const _DR = DisconnectReason || {};
const _LK_REASON_MAP = {
  [_DR.CLIENT_INITIATED]: "client_initiated",
  [_DR.DUPLICATE_IDENTITY]: "duplicate_identity",
  [_DR.SERVER_SHUTDOWN]: "server_shutdown",
  [_DR.PARTICIPANT_REMOVED]: "participant_removed",
  [_DR.ROOM_DELETED]: "room_deleted",
  [_DR.STATE_MISMATCH]: "state_mismatch",
  [_DR.JOIN_FAILURE]: "join_failure",
  [_DR.MIGRATION]: "migration",
  [_DR.SIGNAL_CLOSE]: "signal_close",
  [_DR.ROOM_CLOSED]: "room_closed",
  [_DR.USER_UNAVAILABLE]: "user_unavailable",
  [_DR.USER_REJECTED]: "user_rejected",
  [_DR.SIP_TRUNK_FAILURE]: "sip_trunk_failure",
  [_DR.CONNECTION_TIMEOUT]: "connection_timeout",
};

const _mapLkReason = (reason) => {
  if (reason === undefined || reason === null) return "unknown";
  const mapped = _LK_REASON_MAP[reason];
  return mapped || "unknown";
};

// Verbose tracing — toggled via ?lk=1 URL param (one-shot diagnostic mode).
const lkTrace = (...args) => {
  try {
    if (typeof window === "undefined") return;
    if (window.__rgm_lk_trace === undefined) {
      window.__rgm_lk_trace = new URLSearchParams(window.location.search).has("lk");
    }
    if (window.__rgm_lk_trace) console.log("[LK]", ...args);
  } catch (_e) { /* SSR / sandboxed */ }
};

export const useLiveKitRoom = ({ roomId, enabled = true } = {}) => {
  const roomRef = useRef(null);
  const cancelledRef = useRef(false);
  const attemptsRef = useRef(0);
  // iter_181 (P0) — Intentional-leave latch. Pages call
  // `markIntentional('server_ended' | 'intentional' | ...)` before
  // triggering a disconnect (Leave / End Live / navigate away) so the
  // next Disconnected event is reported to the backend as intentional
  // and does NOT bump the failure counters.
  const intentionalRef = useRef(null);
  // iter_182 (P0) — Per-Room-instance connection session id. Every
  // event we post carries this so the backend can dedupe callbacks that
  // fire multiple times for the SAME real transition (e.g., pagehide
  // + LiveKit Disconnected + effect cleanup all firing on tab close).
  // iter_183 (P0) — session_id + latches now PERSIST across React
  // remounts within the same tab-visit via sessionStorage keyed by
  // roomId. This is the fix for the production incident where a
  // single tab-visit with 6-8 remounts produced 6-8 separate
  // `intentional_leaves` posts instead of 1. See loadOrCreateSession
  // below.
  const sessionIdRef = useRef(null);
  const disconnectedPostedRef = useRef(false);
  const intentionalPostedRef = useRef(false);
  const connectedPostedRef = useRef(false);
  const eventSeqRef = useRef(0);
  // 21.1g+ — track requested facingMode so mobile front/back switch works
  // even when the browser exposes only one logical videoinput device.
  // 21.3a — persisted across re-mounts so a reconnect restores the
  // camera the host explicitly chose (iOS Safari would otherwise revert
  // to the default front camera on every reconnect).
  const _PERSIST_KEY = "rgm.live_facing_mode";
  const facingModeRef = useRef(
    typeof window !== "undefined"
      ? (window.localStorage?.getItem?.(_PERSIST_KEY) === "environment"
        ? "environment" : "user")
      : "user"
  );
  const _persistFacingMode = (m) => {
    facingModeRef.current = m;
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem?.(_PERSIST_KEY, m);
      }
    } catch (_e) { /* sandboxed / privacy mode */ }
  };
  const [state, setState] = useState("idle");
  const [role, setRole] = useState(null);
  const [canPublish, setCanPublish] = useState(false);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [livekitConfigured, setLivekitConfigured] = useState(true);
  const [failedReason, setFailedReason] = useState(null);
  const [attempts, setAttempts] = useState(0);
  // 21.1g+ — bumped whenever LiveKit emits Reconnected, so callers can
  // re-fetch authoritative server state (participants, beam list) from
  // the API instead of trusting only their cached frontend state.
  const [reconnectedAt, setReconnectedAt] = useState(null);
  // 21.1h — bumped on every ParticipantConnected/Disconnected and
  // TrackSubscribed/Unsubscribed so the page can reconcile from the
  // server (eliminates ghosts, missed beam updates, and audio-but-no-
  // video desyncs caused by stale LK state).
  const [participantTick, setParticipantTick] = useState(0);
  // Bumping this triggers the connect effect to retry from scratch.
  const [retryTick, setRetryTick] = useState(0);
  // M2 — active-speaker list for music ducking. Updated by
  // RoomEvent.ActiveSpeakersChanged. Empty list = silence; non-empty =
  // at least one stage participant is talking → room music should duck.
  const [activeSpeakerIds, setActiveSpeakerIds] = useState([]);

  const refreshRemoteList = useCallback((room) => {
    if (!room) return;
    setRemoteParticipants(Array.from(room.remoteParticipants.values()));
  }, []);

  const fetchToken = useCallback(async () => {
    if (!roomId) throw new Error("roomId required");
    const r = await api.post(`/live/rooms/${roomId}/livekit-token`, {});
    lkTrace("token fetched", {
      role: r.data?.role,
      can_publish: r.data?.can_publish,
      ws_url: r.data?.ws_url,
      room: r.data?.room,
      identity: r.data?.identity,
      ttl: r.data?.ttl_minutes,
    });
    return r.data;
  }, [roomId]);

  const refreshToken = useCallback(async () => {
    if (!roomRef.current) return;
    try {
      const tok = await fetchToken();
      try { await roomRef.current.disconnect(true); } catch (_e) { /* ignore */ }
      const room = roomRef.current;
      await room.connect(tok.ws_url, tok.token);
      setRole(tok.role);
      setCanPublish(!!tok.can_publish);
    } catch (e) {
      lkTrace("refreshToken failed", e?.name, e?.message);
      throw e;
    }
  }, [fetchToken]);

  // Phase 21.1f — One-tap reconnect for hosts when auto-retry gave up.
  const retry = useCallback(() => {
    lkTrace("retry requested by user");
    attemptsRef.current = 0;
    setAttempts(0);
    setFailedReason(null);
    setRetryTick((t) => t + 1);
  }, []);

  // iter_183 (P0) — Cross-remount session persistence.
  // ==========================================================
  // Problem this fixes: a single tab-visit to a live room can
  // remount the LiveRoomPage component multiple times (route
  // transitions, parent re-renders, mobile tab visibility churn).
  // Every remount used to generate a fresh `sessionIdRef`, so the
  // backend saw N sessions with N intentional leaves for what the
  // user experienced as ONE visit and ONE Leave click. Production
  // showed 19 intentional_leaves for 3 participants for this reason.
  //
  // Fix: persist { session_id, disconnected_posted, intentional_posted,
  // connected_posted, created_at } in sessionStorage under a key
  // derived from roomId. Every remount within the SAME tab loads the
  // same identity + latches. sessionStorage clears on tab close, so a
  // fresh tab always gets a fresh session.
  //
  // TTL: 30 minutes since last activity. After that, treat as a new
  // visit (rare — most live rooms end within this window).
  const _SESS_KEY = useMemo(
    () => `rgm.live_session.v183.${roomId || "unknown"}`,
    [roomId]
  );
  const _SESS_TTL_MS = 30 * 60 * 1000;

  const _loadSession = useCallback(() => {
    try {
      if (typeof sessionStorage === "undefined") return null;
      const raw = sessionStorage.getItem(_SESS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.id) return null;
      if (parsed.last_seen && Date.now() - parsed.last_seen > _SESS_TTL_MS) {
        sessionStorage.removeItem(_SESS_KEY);
        return null;
      }
      return parsed;
    } catch (_e) { return null; }
  }, [_SESS_KEY, _SESS_TTL_MS]);
  const _saveSession = useCallback((sess) => {
    try {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.setItem(_SESS_KEY, JSON.stringify({ ...sess, last_seen: Date.now() }));
    } catch (_e) { /* privacy mode / quota */ }
  }, [_SESS_KEY]);
  const _clearSession = useCallback(() => {
    try {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.removeItem(_SESS_KEY);
    } catch (_e) { /* ignore */ }
  }, [_SESS_KEY]);
  const _persistLatch = useCallback((patch) => {
    const cur = _loadSession() || {};
    _saveSession({ ...cur, ...patch });
  }, [_loadSession, _saveSession]);

  // iter_181 (P0) — Fire-and-forget client-event beacon. Never throws;
  // errors are swallowed so a network blip during reporting doesn't
  // itself become a source of instability.
  // iter_182 (P0) — Every post carries a stable `session_id` (per
  // Room instance) AND a unique `event_id` (uuid) so the backend can
  // deduplicate callbacks that fire multiple times for the same real
  // transition (pagehide + Disconnected + cleanup on tab close).
  // iter_183 (P0) — Adds explicit `source` field so we can grep the
  // audit trail and see EXACTLY which callback fired each post
  // (markIntentional / livekit_disconnected / pagehide / etc.). The
  // backend also logs a structured line per post for offline analysis.
  const _mkEventId = () => {
    const seq = ++eventSeqRef.current;
    let rand = "";
    try {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        rand = crypto.randomUUID();
      } else {
        rand = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
    } catch (_e) {
      rand = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    return `evt_${seq}_${rand}`;
  };

  const postClientEvent = useCallback((event, reason, intentional = false, source = "unknown") => {
    if (!roomId || !event) return null;
    const eventId = _mkEventId();
    const sessionId = sessionIdRef.current || "sess_unknown";
    try {
      const body = {
        event,
        reason: reason || "unknown",
        intentional: !!intentional,
        session_id: sessionId,
        event_id: eventId,
        source: source || "unknown",
      };
      // Refresh sessionStorage last_seen so the persisted session
      // doesn't TTL-expire mid-visit.
      _persistLatch({});
      api.post(`/live/rooms/${roomId}/client-event`, body).catch(() => {});
      lkTrace("clientEvent →", event, reason, intentional ? "(intentional)" : "", `[${source}]`, eventId);
    } catch (_e) { /* never throws */ }
    return eventId;
  }, [roomId, _persistLatch]);

  // iter_181 (P0) → iter_182 refined → iter_183 remount-safe.
  // Now BOTH the frontend latch AND the sessionStorage-persisted
  // intentional_posted flag guard against re-posting. This means a
  // remount that inherits the persisted latch will short-circuit
  // markIntentional even though the in-memory ref was reset.
  const markIntentional = useCallback((reason = "intentional") => {
    // In-memory guard (same hook instance).
    if (intentionalPostedRef.current || disconnectedPostedRef.current) {
      lkTrace("markIntentional SKIPPED in-memory", reason);
      return;
    }
    // Cross-remount guard (sessionStorage).
    const persisted = _loadSession();
    if (persisted && (persisted.intentional_posted || persisted.disconnected_posted)) {
      lkTrace("markIntentional SKIPPED persisted", reason, persisted);
      // Hydrate in-memory latches so subsequent callbacks in this
      // remount also short-circuit.
      intentionalPostedRef.current = true;
      disconnectedPostedRef.current = true;
      return;
    }
    intentionalRef.current = reason;
    intentionalPostedRef.current = true;
    disconnectedPostedRef.current = true;
    _persistLatch({ intentional_posted: true, disconnected_posted: true });
    postClientEvent("disconnected", reason, true, "markIntentional");
    lkTrace("markIntentional POSTED", reason);
  }, [postClientEvent, _loadSession, _persistLatch]);

  useEffect(() => {
    if (!enabled || !roomId) return;
    cancelledRef.current = false;
    // iter_183 (P0) — Load or create the tab-scoped persisted session.
    // Multiple remounts inside the same tab-visit share the same
    // session_id AND the same latches, so the backend can't see the
    // same real Leave/End Live as N separate sessions.
    let session = _loadSession();
    if (!session || !session.id) {
      let sid;
      try {
        sid = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? `sess_${crypto.randomUUID()}`
          : `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      } catch (_e) {
        sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      }
      session = {
        id: sid,
        disconnected_posted: false,
        intentional_posted: false,
        connected_posted: false,
        created_at: Date.now(),
      };
      _saveSession(session);
      lkTrace("session CREATED", sid);
    } else {
      lkTrace("session RESUMED", session.id, {
        d: session.disconnected_posted,
        i: session.intentional_posted,
        c: session.connected_posted,
      });
    }
    sessionIdRef.current = session.id;
    disconnectedPostedRef.current = !!session.disconnected_posted;
    intentionalPostedRef.current = !!session.intentional_posted;
    connectedPostedRef.current = !!session.connected_posted;
    eventSeqRef.current = 0;
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: { simulcast: true },
      // Phase 21.3a — Seed the persisted facingMode so a reconnect
      // republishes the camera the host explicitly chose.
      videoCaptureDefaults: { facingMode: facingModeRef.current },
    });
    roomRef.current = room;

    const onConn = () => {
      if (cancelledRef.current) return;
      lkTrace("Connected", {
        sid: room.localParticipant?.sid,
        identity: room.localParticipant?.identity,
      });
      attemptsRef.current = 0;
      setAttempts(0);
      setFailedReason(null);
      setState("connected");
      setLocalParticipant(room.localParticipant);
      refreshRemoteList(room);
      // iter_182 (P0) — Only post first successful media connection
      // once per Room instance. Subsequent Connected callbacks (rare,
      // but LiveKit can emit them after an internal migration) are
      // suppressed so we don't inflate the audit stream.
      // iter_183 — also skip if a prior remount already reported this
      // session as connected.
      if (!connectedPostedRef.current) {
        connectedPostedRef.current = true;
        _persistLatch({ connected_posted: true });
        postClientEvent("connected", "unknown", false, "livekit_connected");
      }
    };
    const onReconn = () => {
      if (cancelledRef.current) return;
      lkTrace("Reconnecting…");
      setState("reconnecting");
      postClientEvent("reconnecting", "network", false, "livekit_reconnecting");
    };
    const onReconnDone = () => {
      if (cancelledRef.current) return;
      lkTrace("Reconnected ✓");
      setState("connected");
      setReconnectedAt(Date.now());
      // iter_182 (P0) — Clear the disconnected latch (in-memory AND
      // persisted) so a subsequent HARD drop within this session can
      // still be reported. The backend pairs this reconnected with the
      // preceding disconnected in the same session and downgrades it
      // to a transient.
      disconnectedPostedRef.current = false;
      _persistLatch({ disconnected_posted: false });
      postClientEvent("reconnected", "network", false, "livekit_reconnected");
    };
    const onDisc = (reason) => {
      if (cancelledRef.current) return;
      // iter_182 (P0) — One-shot guard so multiple LiveKit callback
      // paths (SDK-internal disconnect/reconnect cycles, token
      // refresh, effect cleanup) can't each fire an intentional /
      // client_initiated post for the same real transition.
      if (disconnectedPostedRef.current) {
        lkTrace("onDisc SKIPPED (already posted this session)", reason);
        return;
      }
      lkTrace("Disconnected", reason);
      // Classification precedence:
      //  1. Page latched `intentional` via markIntentional()  → intentional=true
      //  2. LiveKit reason CLIENT_INITIATED (user closed room)  → intentional=true
      //  3. LiveKit reason ROOM_DELETED / ROOM_CLOSED / SERVER_SHUTDOWN → server_ended
      //  4. Everything else → network drop (non-intentional failure)
      const mapped = _mapLkReason(reason);
      const latch = intentionalRef.current;
      intentionalRef.current = null;
      let evtReason = latch || mapped;
      let intentional = false;
      if (latch) {
        intentional = true;
      } else if (mapped === "client_initiated") {
        // User closed the tab / navigated away cleanly.
        intentional = true;
        evtReason = "client_initiated";
      } else if (mapped === "room_deleted" || mapped === "room_closed" || mapped === "server_shutdown") {
        // Host ended the room OR server tore it down. Not a failure
        // for viewers — surface as server_ended for clarity.
        evtReason = "server_ended";
        intentional = false;
      } else if (mapped === "unknown") {
        evtReason = "network";
      }
      disconnectedPostedRef.current = true;
      if (intentional) intentionalPostedRef.current = true;
      _persistLatch({
        disconnected_posted: true,
        intentional_posted: intentional ? true : undefined,
      });
      postClientEvent("disconnected", evtReason, intentional, "livekit_disconnected");

      // Grace period on unexpected disconnects.
      setState((cur) => (cur === "connected" || cur === "connecting") ? "reconnecting" : cur);
      setTimeout(() => {
        if (cancelledRef.current) return;
        try {
          const st = room?.state;
          if (st === "disconnected") setState("idle");
        } catch (_e) {
          setState("idle");
        }
      }, 6000);
    };
    const onPart = () => {
      refreshRemoteList(room);
      setParticipantTick((n) => n + 1);
    };
    const onTrack = () => {
      refreshRemoteList(room);
      setParticipantTick((n) => n + 1);
    };

    room.on(RoomEvent.Connected, onConn);
    room.on(RoomEvent.Reconnecting, onReconn);
    room.on(RoomEvent.Reconnected, onReconnDone);
    room.on(RoomEvent.Disconnected, onDisc);
    room.on(RoomEvent.ParticipantConnected, onPart);
    room.on(RoomEvent.ParticipantDisconnected, onPart);
    room.on(RoomEvent.TrackSubscribed, onTrack);
    room.on(RoomEvent.TrackUnsubscribed, onTrack);
    // M2 — expose LiveKit active speakers so LiveRoomPage can duck
    // music volume when any stage participant is talking. Threshold-
    // based (LiveKit emits ActiveSpeakersChanged with the current
    // non-empty talker list; empty list = silence).
    const onActiveSpeakers = (speakers) => {
      if (cancelledRef.current) return;
      try {
        setActiveSpeakerIds(
          (speakers || [])
            .filter((p) => p && p.identity)
            .map((p) => String(p.identity))
        );
      } catch (_e) { /* ignore */ }
    };
    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakers);
    room.on(RoomEvent.LocalTrackPublished, () => {
      if (cancelledRef.current) return;
      const lp = room.localParticipant;
      setCameraEnabled(lp.isCameraEnabled);
      setMicEnabled(lp.isMicrophoneEnabled);
      setLocalParticipant(lp);
    });
    room.on(RoomEvent.LocalTrackUnpublished, () => {
      if (cancelledRef.current) return;
      const lp = room.localParticipant;
      setCameraEnabled(lp.isCameraEnabled);
      setMicEnabled(lp.isMicrophoneEnabled);
      setLocalParticipant(lp);
    });
    room.on(RoomEvent.MediaDevicesError, (e) => {
      lkTrace("MediaDevicesError", e?.name, e?.message);
    });
    room.on(RoomEvent.SignalConnected, () => lkTrace("SignalConnected"));

    const attemptConnect = async () => {
      attemptsRef.current += 1;
      const a = attemptsRef.current;
      setAttempts(a);
      try {
        setState("connecting");
        const tok = await fetchToken();
        if (cancelledRef.current) return;
        if (!tok?.token || !tok?.ws_url) {
          lkTrace("token endpoint returned empty — LiveKit not configured");
          setLivekitConfigured(false);
          setState("idle");
          return;
        }
        setRole(tok.role);
        setCanPublish(!!tok.can_publish);
        lkTrace(`connect() attempt #${a}`, tok.ws_url);
        await room.connect(tok.ws_url, tok.token);
        lkTrace(`connect() resolved attempt #${a}`);
      } catch (e) {
        if (cancelledRef.current) return;
        const reason = e?.response?.data?.detail
          || e?.message || e?.name || "Unknown LiveKit error";
        lkTrace(`connect() attempt #${a} FAILED:`, reason, e);
        // 503 = server-side LiveKit not configured.
        if (e?.response?.status === 503) {
          setLivekitConfigured(false);
          setState("idle");
          return;
        }
        // 409 = room ended.
        if (e?.response?.status === 409) {
          setFailedReason("This live room has ended.");
          setState("failed");
          return;
        }
        // 401 = stale session token. Don't retry — user needs to re-login.
        if (e?.response?.status === 401) {
          setFailedReason("Your session expired. Please refresh and sign in.");
          setState("failed");
          return;
        }
        if (a < MAX_AUTO_RETRIES) {
          const delay = Math.min(8000, 800 * Math.pow(2, a - 1));
          setState("reconnecting");
          lkTrace(`scheduling retry in ${delay}ms`);
          await new Promise((res) => setTimeout(res, delay));
          if (cancelledRef.current) return;
          return attemptConnect();
        }
        setFailedReason(reason);
        setState("failed");
      }
    };

    attemptConnect();

    // iter_182 (P0) — Unified page-teardown beacon. Register ONLY
    // `pagehide` (drop `beforeunload` which fires ALONGSIDE pagehide on
    // tab close, doubling every post). `pagehide` is the modern,
    // reliable teardown signal that works consistently on desktop and
    // mobile Safari (which never fires `beforeunload`). The handler is
    // idempotent: `disconnectedPostedRef` short-circuits any repeat
    // callback so Leave / End Live / LiveKit ROOM_DELETED / this
    // beacon can never each fire a disconnect event for the same
    // teardown.
    const onPageHide = () => {
      if (cancelledRef.current) return;
      if (disconnectedPostedRef.current) return;
      // iter_183 — Also honour the persisted latch. A remount might
      // reset the in-memory ref but the tab-scoped latch survives.
      const persisted = _loadSession();
      if (persisted && persisted.disconnected_posted) return;
      try {
        if (typeof navigator === "undefined" || !navigator.sendBeacon) return;
        const base = api?.defaults?.baseURL || "";
        const url = `${base}/live/rooms/${roomId}/client-event`;
        const latch = intentionalRef.current;
        intentionalRef.current = null;
        disconnectedPostedRef.current = true;
        if (latch) intentionalPostedRef.current = true;
        _persistLatch({
          disconnected_posted: true,
          intentional_posted: latch ? true : undefined,
        });
        const body = JSON.stringify({
          event: "disconnected",
          reason: latch || "page_unload",
          intentional: !!latch,
          session_id: sessionIdRef.current || "sess_unknown",
          event_id: _mkEventId(),
          source: "pagehide",
        });
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } catch (_e) { /* best-effort */ }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onPageHide);
    }
    // iter_182 (P0) — Visibility changes are NO LONGER treated as
    // disconnects. Backgrounding a tab is a normal user action; only
    // LiveKit's own state machine (Reconnecting / Disconnected) is
    // authoritative for a stability incident. We log the visibility
    // transition to the audit stream (via a lightweight event kind) so
    // admins can correlate but the counters are untouched.
    const onVisibility = () => {
      if (cancelledRef.current) return;
      if (typeof document === "undefined") return;
      // No POST — see iter_181 report: visibility posts were adding
      // noise (2 posts per tab-flip × N tab flips) without any counter
      // impact. Removing them cleans up the audit trail.
      lkTrace("visibility →", document.visibilityState);
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelledRef.current = true;
      try {
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibility);
        }
      } catch (_e) { /* ignore */ }
      try {
        if (typeof window !== "undefined") {
          window.removeEventListener("pagehide", onPageHide);
        }
      } catch (_e) { /* ignore */ }
      try { room.disconnect(true); } catch (_e) { /* ignore */ }
      roomRef.current = null;
    };
  }, [roomId, enabled, fetchToken, refreshRemoteList, retryTick, postClientEvent, _loadSession, _persistLatch, _saveSession]);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room || !canPublish) return;
    try {
      const next = !room.localParticipant.isCameraEnabled;
      lkTrace("toggleCamera →", next);
      await room.localParticipant.setCameraEnabled(next);
      setCameraEnabled(next);
      setCameraDenied(false);
    } catch (e) {
      lkTrace("toggleCamera FAILED", e?.name, e?.message);
      if (NOT_ALLOWED.includes(e?.name)) setCameraDenied(true);
    }
  }, [canPublish]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room || !canPublish) return;
    try {
      const next = !room.localParticipant.isMicrophoneEnabled;
      lkTrace("toggleMic →", next);
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicEnabled(next);
      setMicDenied(false);
    } catch (e) {
      lkTrace("toggleMic FAILED", e?.name, e?.message);
      if (NOT_ALLOWED.includes(e?.name)) setMicDenied(true);
    }
  }, [canPublish]);

  const switchCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room || !canPublish) return { ok: false, reason: "no_room" };
    const lp = room.localParticipant;
    if (!lp) return { ok: false, reason: "no_local_participant" };

    // Helper: probe the resulting track's facingMode and return true if
    // it matches `wanted`. Used to detect the iOS-Safari "asked for
    // environment, got user back" flicker.
    const trackFacingMatches = (wanted) => {
      try {
        const pub = lp.getTrackPublication?.(Track.Source.Camera);
        const s = pub?.track?.mediaStreamTrack?.getSettings?.() || {};
        return s.facingMode === wanted;
      } catch (_e) {
        return false;
      }
    };

    // STRATEGY A — desktop / multi-device: try LiveKit's switchActiveDevice
    // when the browser exposes ≥ 2 distinct videoinput deviceIds.
    // Phase 21.4 Batch 6 — Enhanced: when multiple cameras exist AND
    // labels are populated (post-permission), pick by label so we land
    // on the RIGHT camera on Android Chrome + iPadOS Safari.
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const cams = devs.filter((d) => d.kind === "videoinput" && d.deviceId);
      if (cams.length >= 2 && lp.switchActiveDevice) {
        const pub = lp.getTrackPublication?.(Track.Source.Camera);
        const currentId =
          pub?.track?.mediaStreamTrack?.getSettings?.()?.deviceId;
        const wantEnvironment =
          facingModeRef.current !== "environment";
        const labelMatches = (label, wantEnv) => {
          const l = (label || "").toLowerCase();
          return wantEnv
            ? /(back|rear|environment|arriere|外|后)/.test(l)
            : /(front|user|selfie|facetime|iphone.*front|face|前)/.test(l);
        };
        // Prefer a label-matched cam different from currentId.
        let next = cams.find((c) => c.deviceId !== currentId
          && labelMatches(c.label, wantEnvironment));
        // Fallback: any camera that's not the current one.
        if (!next) next = cams.find((c) => c.deviceId !== currentId);
        if (!next) next = cams[0];
        await lp.switchActiveDevice("videoinput", next.deviceId);
        _persistFacingMode(wantEnvironment ? "environment" : "user");
        lkTrace("switchCamera via switchActiveDevice → deviceId=",
          next.deviceId, "label=", next.label,
          "wantEnv=", wantEnvironment);
        return {
          ok: true, matched: true, strategy: "deviceId",
          requested: wantEnvironment ? "environment" : "user",
          actual: wantEnvironment ? "environment" : "user",
          label: next.label || null,
        };
      }
    } catch (e) {
      lkTrace("switchActiveDevice failed, falling back to facingMode", e?.name);
    }

    // STRATEGY B — mobile single-device: flip facingMode. iOS Safari +
    // Android Chrome both need the previous camera track fully stopped
    // BEFORE the new one is requested or the browser hands back the
    // cached front camera ("flicker back to front" bug). We also probe
    // the resulting track and retry with `{exact:...}` if the loose
    // constraint silently fell back.
    const cur = facingModeRef.current === "environment" ? "environment" : "user";
    const next = cur === "user" ? "environment" : "user";
    try {
      _persistFacingMode(next);
      lkTrace("switchCamera via facingMode →", next);

      // (1) Fully stop the previous camera track. iOS Safari is the worst
      // offender — it caches the MediaStream and reuses it on the next
      // setCameraEnabled call unless we explicitly stop + unpublish.
      try {
        const pub = lp.getTrackPublication?.(Track.Source.Camera);
        const t = pub?.track?.mediaStreamTrack;
        if (t) {
          try { t.stop(); } catch (_e) { /* defensive */ }
        }
        await lp.setCameraEnabled(false);
      } catch (_e) { /* may already be off */ }

      // Phase 21.4 Batch 4 — Add a release delay between disable and
      // re-enable so iOS Safari / Android Chrome fully release the
      // previous camera hardware before the new one is requested.
      // Without this, both browsers routinely hand back the cached
      // front camera stream even when facingMode:'environment' is asked.
      await new Promise((res) => setTimeout(res, 400));

      // (2) Update room defaults so any future republish (e.g., on
      // reconnect) keeps the chosen facingMode.
      try {
        room.options = room.options || {};
        room.options.videoCaptureDefaults = {
          ...(room.options.videoCaptureDefaults || {}),
          facingMode: next,
        };
      } catch (_e) { /* defensive */ }

      // (3) Re-enable with the loose constraint first (Android Chrome
      // and some iOS versions reject `{exact}` outright).
      await lp.setCameraEnabled(true, { facingMode: next });

      // (4) Verify the resulting track actually matches. If iOS Safari
      // handed back the wrong camera, retry once with `{exact}`.
      let matched = trackFacingMatches(next);
      if (!matched) {
        lkTrace("facingMode flicker detected, retrying with exact constraint");
        try {
          await lp.setCameraEnabled(false);
          // Additional release delay before the exact-constraint retry.
          await new Promise((res) => setTimeout(res, 350));
          await lp.setCameraEnabled(true, {
            facingMode: { exact: next },
          });
          matched = trackFacingMatches(next);
        } catch (e2) {
          lkTrace("exact-constraint retry FAILED", e2?.name, e2?.message);
          // Phase 21.4 Batch 5 — revert persisted facingMode so a UI
          // retry doesn't lock the user into the wrong direction.
          _persistFacingMode(cur);
          setCameraEnabled(true);
          return { ok: false, matched: false, reason: "exact_retry_failed",
                   requested: next, actual: cur };
        }
      }
      setCameraEnabled(true);
      // If we ended up with the wrong facing mode after retry, revert
      // the persisted preference so the next tap goes back the other way.
      if (!matched) {
        _persistFacingMode(cur);
      }
      // Phase 21.4 Batch 8 — iOS Safari LAST-RESORT bypass. If LiveKit's
      // own setCameraEnabled path never landed the requested facing mode
      // (typical iOS Safari behavior where the SDK re-uses the cached
      // camera track), grab a fresh MediaStreamTrack directly via
      // getUserMedia and publish it as a replacement camera track.
      if (!matched) {
        try {
          lkTrace("iOS bypass: fetching direct getUserMedia stream", next);
          // Ensure any existing camera track is fully released first.
          try {
            const pub = lp.getTrackPublication?.(Track.Source.Camera);
            const t = pub?.track?.mediaStreamTrack;
            if (t) { try { t.stop(); } catch (_e) { /* defensive */ } }
            await lp.setCameraEnabled(false);
          } catch (_e) { /* defensive */ }
          await new Promise((res) => setTimeout(res, 300));

          // Try `exact` first (iOS 15+ generally accepts this).
          let ms = null;
          try {
            ms = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: { exact: next } },
              audio: false,
            });
          } catch (_e1) {
            // Fallback to `ideal`.
            try {
              ms = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: next } },
                audio: false,
              });
            } catch (_e2) { ms = null; }
          }
          if (ms) {
            const rawTrack = ms.getVideoTracks()[0];
            if (rawTrack) {
              // Prefer LiveKit's helper if available; otherwise wrap raw.
              let lkTrack = null;
              try {
                lkTrack = new LocalVideoTrack(rawTrack);
              } catch (_e3) { lkTrack = null; }
              if (lkTrack) {
                try {
                  await lp.publishTrack(lkTrack, {
                    source: Track.Source.Camera,
                    name: "camera",
                  });
                  matched = trackFacingMatches(next);
                  if (matched) _persistFacingMode(next);
                  lkTrace("iOS bypass: published direct track",
                    "matched=", matched);
                } catch (e4) {
                  lkTrace("iOS bypass publishTrack FAILED",
                    e4?.name, e4?.message);
                }
              }
            }
          }
        } catch (bypassErr) {
          lkTrace("iOS bypass FAILED", bypassErr?.name, bypassErr?.message);
        }
      }
      return { ok: matched, matched, requested: next,
               actual: matched ? next : cur };
    } catch (e) {
      lkTrace("facingMode switch FAILED", e?.name, e?.message);
      // Phase 21.4 Batch 5 — revert persisted facingMode on hard fail.
      _persistFacingMode(cur);
      return { ok: false, matched: false, reason: e?.name || "unknown",
               requested: next, actual: cur };
    }
  }, [canPublish]);

  return {
    state, role, canPublish,
    localParticipant, remoteParticipants,
    toggleCamera, toggleMic, switchCamera,
    cameraEnabled, micEnabled,
    cameraDenied, micDenied,
    refreshToken,
    livekitConfigured,
    // Phase 21.1f
    retry,
    failedReason,
    attempts,
    // Phase 21.1g+ — Reconciliation trigger for the page
    reconnectedAt,
    // Phase 21.1h — bumped on participant/track events for sync reconcile
    participantTick,
    // iter_181 (P0) — Intentional-leave flag so pages can suppress the
    // failure counter bump when the user clicks Leave / End Live.
    markIntentional,
    // M2 — current set of LiveKit speaker identities (empty when
    // silent). The page uses this to duck Beam Music volume.
    activeSpeakerIds,
  };
};

export default useLiveKitRoom;
